import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, TouchableOpacity, ViewStyle } from 'react-native';

import { useTheme } from '@/components/theme-provider';

const MIN_SPIN_MS = 1000;

interface RefreshButtonProps {
	onRefresh: () => void | Promise<unknown>;
	size?: number;
	style?: ViewStyle;
	accessibilityLabel?: string;
	testID?: string;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
	onRefresh,
	size = 24,
	style,
	accessibilityLabel = 'Refresh',
	testID,
}) => {
	const theme = useTheme();
	const [spinning, setSpinning] = useState(false);
	const rotation = useRef(new Animated.Value(0)).current;

	useEffect(() => {
		if (!spinning) {
			rotation.setValue(0);
			return;
		}
		const loop = Animated.loop(
			Animated.timing(rotation, {
				toValue: 1,
				duration: 900,
				easing: Easing.linear,
				useNativeDriver: true,
			}),
		);
		loop.start();
		return () => loop.stop();
	}, [spinning, rotation]);

	const handlePress = useCallback(async () => {
		if (spinning) return;
		setSpinning(true);
		const minDelay = new Promise<void>(resolve => setTimeout(resolve, MIN_SPIN_MS));
		try {
			await Promise.all([Promise.resolve(onRefresh()), minDelay]);
		} finally {
			setSpinning(false);
		}
	}, [onRefresh, spinning]);

	const rotate = rotation.interpolate({
		inputRange: [0, 1],
		outputRange: ['0deg', '360deg'],
	});

	const buttonStyle: ViewStyle = {
		width: size + 16,
		height: size + 16,
		borderRadius: (size + 16) / 2,
		justifyContent: 'center',
		alignItems: 'center',
	};

	return (
		<TouchableOpacity
			style={[buttonStyle, style]}
			onPress={handlePress}
			activeOpacity={0.7}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityState={{ busy: spinning }}
			testID={testID}
		>
			<Animated.View style={{ transform: [{ rotate }] }}>
				<Ionicons name="refresh-outline" size={size} color={theme.colors.onSurface} />
			</Animated.View>
		</TouchableOpacity>
	);
};
